import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useUser } from "../Context/UserContext.js";
import { createRoute } from "../routing/createRoute.js";
import { isQuickCreateEnabled, isTypingInto } from "./quick_create.js";
import { runQuickCreateMca } from "./quickCreateMca.js";

/**
 * Listens for Option+Shift+M (Mac) / Alt+Shift+M (Windows/Linux) while mounted
 * and creates an MCA incident with stub data, ready to share.
 *
 * Does nothing unless `isQuickCreateEnabled` and the session organisation uses
 * the MCA form, so the chord is left to the browser otherwise.
 */
export function useQuickCreateMca() {
  const navigate = useNavigate();
  const { user } = useUser();
  const isRunningRef = useRef(false);
  const isMentalCapacityActEnabled =
    user?.sessionOrganisationConfiguration?.mentalCapacityActEnabled === true;

  const createIncident = useCallback(async () => {
    if (!user) {
      console.warn("Quick create MCA: no user available");
      return;
    }
    if (isRunningRef.current) {
      console.warn("Quick create MCA: already in progress");
      return;
    }
    isRunningRef.current = true;

    try {
      const incidentWorkItemId = await runQuickCreateMca({ user });
      navigate(createRoute.incidentProgressPage(incidentWorkItemId));
    } catch (error) {
      console.error("Quick create MCA failed:", error);
    } finally {
      isRunningRef.current = false;
    }
  }, [navigate, user]);

  useEffect(() => {
    if (!isQuickCreateEnabled || !isMentalCapacityActEnabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Not Cmd/Ctrl+Shift+M, which browsers keep for themselves. Matched on
      // code rather than key: the character Option+Shift+M produces varies by
      // keyboard layout, the physical key does not.
      const isChord =
        event.code === "KeyM" &&
        event.altKey &&
        event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey;

      if (!isChord || isTypingInto(document.activeElement)) {
        return;
      }

      event.preventDefault();
      void createIncident();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [createIncident, isMentalCapacityActEnabled]);
}
